import { Box, CircularProgress, Typography, Button } from '@mui/material';
import { PyrenzModal, PyrenzModalContent } from '@client/theme';
import { PayPalButtons } from '@paypal/react-paypal-js';
import { Utils as utils } from '@client/utility';
import { usePyrenzAlert } from '@renderer/provider';
import { useState } from 'react';
import { useUserStore } from '@client/store';

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  plan: {
    title: string;
    price: number;
    baseCredits: number;
    bonusCredits?: number;
  } | null;
}

interface BackendResponse {
  status: 'success' | 'error';
  purchase_id?: string;
  credits_added?: number;
  new_balance?: number;
  invoice?: {
    invoice_id: string;
    invoice_number: string;
    status: string;
    href: string;
  };
  error?: string;
  details?: any;
}

export function PaymentModal({ isOpen, onClose, plan }: PaymentModalProps) {
  if (!plan) return null;

  const showAlert = usePyrenzAlert();
  const [loading, setLoading] = useState(false);
  const [invoiceData, setInvoiceData] = useState<BackendResponse['invoice'] | null>(null);
  const [purchaseComplete, setPurchaseComplete] = useState(false);
  const addCredits = useUserStore((state) => state.addCredits);
  const setPendingPurchase = useUserStore((state) => state.setPendingPurchase);
  const userUUID = useUserStore((state) => state.userUUID);

  const totalCredits = plan.baseCredits + (plan.bonusCredits || 0);

  const handleCreateOrder = async () => {
    try {
      if (!userUUID) {
        showAlert('Please log in to purchase credits', 'error');
        throw new Error('User not authenticated');
      }

      const res = await utils.post<{ order_id: string; status: string; error?: string; details?: any }>(
        '/webhook/paypal?type=create',
        {
          credits: plan.baseCredits,
          amount: plan.price,
          user_uuid: userUUID,
        }
      );

      if (res.error) {
        throw new Error(res.error);
      }

      if (res.order_id) {
        setPendingPurchase({ credits: totalCredits, amount: plan.price });
        return res.order_id;
      }
      
      throw new Error('Failed to create order: No order_id received');
    } catch (err: any) {
      const errorMsg = err?.message || err?.response?.data?.error || 'Failed to initialize payment';
      showAlert(errorMsg, 'error');
      throw err; 
    }
  };

  const handleOnApprove = async (data: any) => {
    setLoading(true);
    try {
      if (!userUUID) {
        showAlert('User session expired. Please log in again.', 'error');
        return;
      }

      const res = await utils.post<BackendResponse>('/webhook/paypal?type=credits', {
        order_id: data.orderID,
        credits: plan.baseCredits,
        amount: plan.price,
        user_uuid: userUUID,
      });

      if (res.status === 'success') {
        addCredits(res.credits_added || totalCredits);
        setPendingPurchase(null);
        setPurchaseComplete(true);
        
        if (res.invoice) {
          setInvoiceData(res.invoice);
        }
        
        showAlert(`Successfully purchased ${totalCredits.toLocaleString()} credits!`, 'success');
      } else {
        showAlert(res.error || 'Purchase failed', 'error');
      }
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'message' in err
          ? (err as any).message
          : 'There was an issue completing your purchase.';
      showAlert(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOnError = () => {
    setPendingPurchase(null);
  };

  const handleOnCancel = () => {
    setPendingPurchase(null);
  };

  const handleClose = () => {
    setInvoiceData(null);
    setPurchaseComplete(false);
    onClose();
  };

  const handleViewInvoice = () => {
    if (invoiceData?.href) {
      window.open(invoiceData.href, '_blank');
    }
  };

  return (
    <PyrenzModal open={isOpen} onClose={handleClose}>
      <PyrenzModalContent>
        {!purchaseComplete ? (
          <>
            <Typography variant="h6" gutterBottom>
              Purchase {plan.baseCredits.toLocaleString()} Credits
            </Typography>

            <Box sx={{ mt: 2, mb: 3 }}>
              <Typography variant="body1" color="text.secondary">
                You will receive:
              </Typography>
              
              <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                {plan.baseCredits.toLocaleString()} Credits
              </Typography>
              
              {plan.bonusCredits && plan.bonusCredits > 0 && (
                <Typography variant="h6" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                  + {plan.bonusCredits.toLocaleString()} Bonus
                </Typography>
              )}
              
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Total: {totalCredits.toLocaleString()} credits for ${plan.price.toFixed(2)} USD
              </Typography>
            </Box>

            <Box
              sx={{
                mt: 3,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
              }}
            >
              {loading && <CircularProgress color="primary" />}
              <PayPalButtons
                style={{
                  layout: 'vertical',
                  color: 'gold',
                  shape: 'rect',
                  label: 'paypal',
                }}
                createOrder={handleCreateOrder}
                onApprove={handleOnApprove}
                onError={handleOnError}
                onCancel={handleOnCancel}
              />
            </Box>
          </>
        ) : (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h5" sx={{ color: 'success.main', fontWeight: 'bold', mb: 2 }}>
              Purchase Complete!
            </Typography>
            
            <Typography variant="body1" sx={{ mb: 3 }}>
              You received {totalCredits.toLocaleString()} credits
            </Typography>

            {invoiceData ? (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                <Typography variant="h6" gutterBottom>
                  Invoice Generated
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Invoice #{invoiceData.invoice_number} has been sent to your email
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleViewInvoice}
                  sx={{ mr: 1 }}
                >
                  View Invoice
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleClose}
                >
                  Close
                </Button>
              </Box>
            ) : (
              <Button
                variant="contained"
                onClick={handleClose}
                sx={{ mt: 2 }}
              >
                Close
              </Button>
            )}
          </Box>
        )}
      </PyrenzModalContent>
    </PyrenzModal>
  );
}